import React from 'react';
import {Box, Typography} from '@mui/material';
import {BarChart} from '@mui/x-charts/BarChart';
import {LineChart} from '@mui/x-charts/LineChart';
import {Trans} from '@lingui/react/macro';
import {SplitRow} from '../../../types/models/Split';

type ParetoChartProps = {
    rows: SplitRow[],
    /** What the volumes are, for the caption under the chart. */
    measure?: 'shipped' | 'produced',
    height?: number,
    /** How many bars to draw before the rest is folded into one. */
    topN?: number,
};

// The two charts are laid over each other, so they have to agree on the room
// they take. Every axis therefore gets the same width and height on both
// sides, and the axis the other chart does not draw is still reserved.
const MARGIN = {left: 20, right: 20, top: 20, bottom: 20};
const LEFT_AXIS_WIDTH = 70;
const RIGHT_AXIS_WIDTH = 55;
const X_AXIS_HEIGHT = 90;

/** An axis that takes up its room but draws nothing. */
const HIDDEN_AXIS = {
    disableLine: true,
    disableTicks: true,
    tickLabelStyle: {opacity: 0},
};

/**
 * A Pareto chart: the volume of each of the parties, biggest first, with the
 * running share of the total drawn over it.
 */
const ParetoChart = ({rows, measure = 'shipped', height = 400, topN = 12}: ParetoChartProps) => {

    const data = React.useMemo(() => {
        const sorted = [...rows].sort((a, b) => b.volume - a.volume);
        if (sorted.length <= topN) {
            return sorted;
        }
        const head = sorted.slice(0, topN - 1);
        const tail = sorted.slice(topN - 1);
        const total = sorted.reduce((acc, row) => acc + row.volume, 0);
        const tailVolume = tail.reduce((acc, row) => acc + row.volume, 0);
        const headVolume = head.reduce((acc, row) => acc + row.volume, 0);
        return [
            ...head,
            {
                key: 'others',
                label: 'Others',
                volume: tailVolume,
                share: total > 0 ? (tailVolume / total) * 100 : 0,
                cumulative: total > 0 ? ((headVolume + tailVolume) / total) * 100 : 0,
            },
        ];
    }, [rows, topN]);

    if (data.length === 0) {
        return (
            <Typography variant="body2" data-testid="Pareto-empty">
                <Trans>No data</Trans>
            </Typography>
        );
    }

    const labels = data.map(row => row.label);
    const volumes = data.map(row => row.volume);
    const cumulative = data.map(row => row.cumulative);

    const xAxis = {
        scaleType: 'band' as const,
        data: labels,
        categoryGapRatio: 0.2,
        height: X_AXIS_HEIGHT,
        tickLabelStyle: {textAnchor: 'middle' as const, fontSize: 10},
    };

    return (
        <Box sx={{position: 'relative', width: '100%'}} data-testid="Pareto-chart">
            <BarChart
                xAxis={[xAxis]}
                yAxis={[
                    {position: 'left' as const, width: LEFT_AXIS_WIDTH, label: 'Volume (m³)'},
                    // The right axis belongs to the line, but the room for it
                    // has to be reserved here as well.
                    {...HIDDEN_AXIS, position: 'right' as const, width: RIGHT_AXIS_WIDTH},
                ]}
                series={[{
                    data: volumes,
                    label: 'Volume (m³)',
                    color: '#1976d2',
                }]}
                height={height}
                margin={MARGIN}
                slotProps={{legend: {sx: {display: 'none'}}}}
            />
            <Box sx={{position: 'absolute', inset: 0, pointerEvents: 'none'}}>
                <LineChart
                    xAxis={[{
                        ...xAxis,
                        // The labels belong to the bars; the line only needs
                        // the room they take.
                        tickLabelStyle: {opacity: 0},
                        disableLine: true,
                        disableTicks: true,
                    }]}
                    yAxis={[
                        // The left axis belongs to the bars, but the room for
                        // it has to be reserved here as well.
                        {...HIDDEN_AXIS, position: 'left' as const, width: LEFT_AXIS_WIDTH},
                        {
                            position: 'right' as const,
                            width: RIGHT_AXIS_WIDTH,
                            valueFormatter: (value: number) => `${value.toFixed(0)} %`,
                        },
                    ]}
                    series={[{
                        data: cumulative,
                        label: 'Cumulative %',
                        color: '#d32f2f',
                        showMark: true,
                        curve: 'linear',
                    }]}
                    height={height}
                    margin={MARGIN}
                    slotProps={{legend: {sx: {display: 'none'}}}}
                />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{display: 'block', textAlign: 'right'}}>
                {measure === 'shipped'
                    ? <Trans>Volume of the timber that left the storage</Trans>
                    : <Trans>Volume of everything that was produced</Trans>}
            </Typography>
        </Box>
    );
};

export default ParetoChart;
